/**
 * /admin/questions/review — the pending-question queue.
 *
 * This screen is a **gate**, not a viewer. Everything a model or an import
 * produced lands here, and the API refuses to approve two kinds of question:
 *
 *   * a NAT question whose answer key no human has confirmed — the upstream
 *     sources truncate decimals instead of rounding (a true 3.567 is published
 *     as 3.56), so an unchecked key is a wrong answer waiting to be marked
 *     wrong in a real exam;
 *   * a question referring to a figure that was never stored, which the student
 *     would meet as an unanswerable question.
 *
 * So `key_verified: true` is only ever sent from an explicit "I have checked
 * this myself" click — never folded into the approve payload, never inferred,
 * and never granted by a bulk action. When the API refuses, its own message is
 * shown verbatim rather than reworded here.
 *
 * Speed still matters — this is a backlog. Flagged questions come first, one at
 * a time, rendered as the student would see them, with every action on a key:
 *
 *   A       approve (blocked while a gate is open)   E   edit
 *   R       reject                                   S   skip
 *   J / K   next / previous                          ⌘↩  save an edit
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ImageOff,
  Pencil,
  SkipForward,
  Trash2,
  TriangleAlert,
  Upload,
  X,
} from "lucide-react";
import { Badge, Button, Card, Input, Select, useToast } from "@/components/ui";
import { cn } from "@/lib/cn";
import { Difficulty } from "@exam/shared";
import type { CodeTest } from "@exam/shared";
import { DIFFICULTY_LABEL, labelFor } from "./lib/enumLabels";
import type {
  CorrectAnswer,
  QuestionWithContext,
  ReviewQueueItem,
  ReviewQuestionInput,
} from "@exam/shared";
import { QuestionMeta } from "./components/QuestionMeta";
import { QuestionView, describeAnswer } from "./components/QuestionView";
import { ErrorNote, FixtureBanner, Loading, PageTitle } from "./components/Shell";
import {
  deleteQuestionFigure,
  fetchReviewQueue,
  reviewQuestion,
  setTopicVideo,
  updateQuestion,
  uploadQuestionFigure,
} from "./data/client";
import { useAsync } from "./lib/useAsync";

const SHORTCUTS: Array<{ keys: string; label: string }> = [
  { keys: "A", label: "Approve" },
  { keys: "R", label: "Reject" },
  { keys: "E", label: "Edit" },
  { keys: "S", label: "Skip" },
  { keys: "J / K", label: "Next / previous" },
];

export function ReviewQueuePage() {
  const { toast } = useToast();
  const queue = useAsync(() => fetchReviewQueue(200, 0), []);

  const [items, setItems] = useState<ReviewQueueItem[]>([]);
  const [index, setIndex] = useState(0);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  /** Ids decided this session, so the counter reads honestly. */
  const [done, setDone] = useState<Set<string>>(new Set());
  /** The API's own refusal, shown verbatim. */
  const [refusal, setRefusal] = useState<string | null>(null);
  /** Gate state for the current question; reset whenever it changes. */
  const [keyChecked, setKeyChecked] = useState(false);

  useEffect(() => {
    if (!queue.data) return;
    // Flagged first — that is where the time actually goes. `sort` is stable,
    // so the server's oldest-first order survives within each group.
    setItems(
      [...queue.data.questions].sort(
        (a, b) => Number(b.needs_attention) - Number(a.needs_attention),
      ),
    );
    setIndex(0);
  }, [queue.data]);

  const visible = useMemo(
    () => (onlyFlagged ? items.filter((i) => i.needs_attention) : items),
    [items, onlyFlagged],
  );

  const current = visible[index];
  const currentId = current?.question.id;

  useEffect(() => {
    setKeyChecked(false);
    setRefusal(null);
    setEditing(false);
  }, [currentId]);

  const advance = useCallback(() => {
    setIndex((i) => Math.min(i + 1, Math.max(0, visible.length - 1)));
  }, [visible.length]);

  const replaceItem = useCallback((updated: QuestionWithContext) => {
    setItems((prev) =>
      prev.map((item) =>
        item.question.id === updated.id ? { ...item, question: updated } : item,
      ),
    );
  }, []);

  /* ---------------------------------------------------------------- */
  /* Gates                                                             */
  /* ---------------------------------------------------------------- */

  const needsKeyCheck =
    current !== undefined &&
    current.question.type === "NAT" &&
    !current.question.key_verified;

  const needsFigure =
    current !== undefined &&
    current.question.requires_figure &&
    current.question.figure_r2_key === null;

  const blockedReason = !current
    ? null
    : needsKeyCheck && !keyChecked
      ? "Confirm you have checked the numeric answer key first."
      : needsFigure
        ? "Add the figure, or clear the figure requirement, first."
        : null;

  const act = useCallback(
    async (action: "approve" | "reject") => {
      if (!current || busy) return;
      if (action === "approve" && blockedReason) {
        setRefusal(blockedReason);
        return;
      }

      setBusy(true);
      setRefusal(null);
      try {
        const input: ReviewQuestionInput = { action };
        // Set only by the explicit confirmation — never by default.
        if (action === "approve" && needsKeyCheck && keyChecked) {
          input.key_verified = true;
        }
        const updated = await reviewQuestion(current.question.id, input);
        replaceItem(updated);
        setDone((prev) => new Set(prev).add(updated.id));
        toast({
          tone: action === "approve" ? "success" : "neutral",
          title: action === "approve" ? "Approved" : "Rejected",
          description: plainTitle(current.question.body_md),
        });
        advance();
      } catch (err) {
        // The API's refusals are written for a human — do not reword them.
        setRefusal(err instanceof Error ? err.message : "That did not work.");
      } finally {
        setBusy(false);
      }
    },
    [
      advance,
      blockedReason,
      busy,
      current,
      keyChecked,
      needsKeyCheck,
      replaceItem,
      toast,
    ],
  );

  // Global shortcuts, suspended while editing so typing does not fire actions.
  useEffect(() => {
    if (editing) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable)
      ) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case "a":
        case "arrowright":
          event.preventDefault();
          void act("approve");
          break;
        case "r":
          event.preventDefault();
          void act("reject");
          break;
        case "e":
          event.preventDefault();
          setEditing(true);
          break;
        case "s":
        case "j":
          event.preventDefault();
          advance();
          break;
        case "k":
        case "arrowleft":
          event.preventDefault();
          setIndex((i) => Math.max(0, i - 1));
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [act, advance, editing]);

  const summary = queue.data?.summary;

  return (
    <div className="space-y-4">
      <PageTitle
        title="Review queue"
        meta={
          visible.length === 0
            ? "Nothing pending."
            : `${index + 1} of ${visible.length}${
                done.size > 0 ? ` · ${done.size} decided this session` : ""
              }`
        }
        actions={
          <Link to="/admin/questions">
            <Button>Back to the bank</Button>
          </Link>
        }
      />

      <FixtureBanner />

      {summary ? (
        <Card>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
            <span className="tabular text-xs text-ink">
              <span className="font-semibold">{summary.pending}</span> pending
            </span>
            <SummaryChip count={summary.unverified_key} label="answer key unconfirmed" />
            <SummaryChip count={summary.missing_figure} label="figure missing" />
            <SummaryChip
              count={summary.low_confidence}
              label="source unsure of its answer"
            />
            <label className="ml-auto flex items-center gap-2 text-xs text-ink-muted">
              <input
                type="checkbox"
                checked={onlyFlagged}
                onChange={(e) => {
                  setOnlyFlagged(e.target.checked);
                  setIndex(0);
                }}
                className="size-3.5 accent-accent"
              />
              Only questions needing attention
            </label>
          </div>
        </Card>
      ) : null}

      {queue.loading ? (
        <Loading />
      ) : queue.error ? (
        <ErrorNote error={queue.error} />
      ) : visible.length === 0 ? (
        <Card>
          <p className="px-4 py-6 text-xs text-ink-faint">
            {onlyFlagged
              ? "Nothing in the queue is flagged."
              : "No questions are waiting for review."}
          </p>
        </Card>
      ) : current ? (
        <Card>
          <div className="flex items-start justify-between gap-4 border-b border-border px-4 py-3">
            <div className="min-w-0 space-y-1.5">
              <QuestionMeta question={current.question} showStatus={false} />
              {current.reasons.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {current.reasons.map((reason) => (
                    <Badge key={reason} tone="violation">
                      <TriangleAlert className="size-3" />
                      {reason}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-2xs text-ink-faint">Nothing flagged on this one.</p>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                icon={<ChevronLeft />}
                disabled={index === 0}
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
                aria-label="Previous question"
              />
              <span className="tabular w-16 text-center text-2xs text-ink-faint">
                {index + 1} / {visible.length}
              </span>
              <Button
                size="sm"
                variant="ghost"
                icon={<ChevronRight />}
                disabled={index >= visible.length - 1}
                onClick={advance}
                aria-label="Next question"
              />
            </div>
          </div>

          {refusal ? (
            <div className="flex items-start gap-2 border-b border-border bg-violation-soft px-4 py-2">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-violation" />
              <p className="text-xs text-ink">{refusal}</p>
            </div>
          ) : null}

          <div className="p-4">
            {editing ? (
              <QuestionEditor
                question={current.question}
                busy={busy}
                onCancel={() => setEditing(false)}
                onSave={async (patch) => {
                  setBusy(true);
                  try {
                    const updated = await updateQuestion(current.question.id, patch);
                    replaceItem(updated);
                    setEditing(false);
                    toast({ tone: "success", title: "Question updated." });
                  } catch (err) {
                    setRefusal(
                      err instanceof Error ? err.message : "Could not save the edit.",
                    );
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            ) : (
              <div className="space-y-4">
                {needsKeyCheck ? (
                  <KeyCheckGate
                    question={current.question}
                    checked={keyChecked}
                    onChange={setKeyChecked}
                  />
                ) : null}

                {needsFigure ? (
                  <FigureGate
                    question={current.question}
                    busy={busy}
                    onChanged={replaceItem}
                    onError={setRefusal}
                  />
                ) : null}

                <QuestionView
                  question={current.question}
                  showAnswer
                  onFigureRefreshed={replaceItem}
                />

                {current.question.figure_r2_key !== null ? (
                  <FigureActions
                    question={current.question}
                    busy={busy}
                    onChanged={replaceItem}
                    onError={setRefusal}
                  />
                ) : null}
              </div>
            )}
          </div>

          {!editing ? (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="primary"
                  icon={<Check />}
                  disabled={busy || blockedReason !== null}
                  title={blockedReason ?? undefined}
                  onClick={() => void act("approve")}
                >
                  Approve
                </Button>
                <Button
                  variant="danger"
                  icon={<X />}
                  disabled={busy}
                  onClick={() => void act("reject")}
                >
                  Reject
                </Button>
                <Button icon={<Pencil />} onClick={() => setEditing(true)}>
                  Edit
                </Button>
                <Button variant="ghost" icon={<SkipForward />} onClick={advance}>
                  Skip
                </Button>
                {blockedReason ? (
                  <span className="text-2xs text-ink-muted">{blockedReason}</span>
                ) : null}
              </div>

              <div className="hidden items-center gap-3 xl:flex">
                {SHORTCUTS.map((shortcut) => (
                  <span
                    key={shortcut.keys}
                    className="flex items-center gap-1 text-2xs text-ink-faint"
                  >
                    <kbd className="rounded-xs border border-border-strong bg-surface-sunken px-1 font-sans">
                      {shortcut.keys}
                    </kbd>
                    {shortcut.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}

function SummaryChip({ count, label }: { count: number; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs">
      <span
        className={
          count > 0
            ? "tabular font-semibold text-violation"
            : "tabular font-semibold text-ink-faint"
        }
      >
        {count}
      </span>
      <span className="text-ink-muted">{label}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Gate: the numeric answer key                                        */
/* ------------------------------------------------------------------ */

/**
 * The confirmation that unlocks approving a NAT question. It puts the key and
 * the reason for doubt in front of you — a confirmation you cannot act on is
 * theatre, so the value being attested to is right here, not a scroll away.
 */
function KeyCheckGate({
  question,
  checked,
  onChange,
}: {
  question: QuestionWithContext;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="rounded-md border border-review/30 bg-review-soft p-3">
      <div className="flex items-start gap-2">
        <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-review" />
        <div className="min-w-0 space-y-2">
          <p className="text-xs font-medium text-ink">
            This numeric answer key has not been checked by a person.
          </p>
          <p className="text-2xs text-ink-muted">
            The sources this came from truncate decimals instead of rounding
            them — a true 3.567 is published as 3.56. Work the answer out,
            compare it with the key below, and only then confirm.
          </p>

          <div className="flex flex-wrap items-baseline gap-3 rounded-sm border border-border bg-surface px-2.5 py-1.5">
            <span className="text-2xs text-ink-muted">Key</span>
            <span className="tabular text-sm font-medium text-ink">
              {describeAnswer(question.correct_answer)}
            </span>
            {question.confidence === "low" ? (
              <span className="text-2xs text-violation">
                the source marked this answer uncertain
              </span>
            ) : null}
          </div>

          <label className="flex items-start gap-2 text-xs text-ink">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => onChange(e.target.checked)}
              className="mt-0.5 size-3.5 accent-accent"
            />
            I have checked this numeric answer myself.
          </label>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Gate: the missing figure                                            */
/* ------------------------------------------------------------------ */

/**
 * Three ways out, in the order to reach for them: upload the figure, point at
 * one already in R2, or record that the question does not actually need one.
 * Uploading is the primary path; if that endpoint is not deployed yet the
 * attempt fails with its own message and the other two still work.
 */
function FigureGate({
  question,
  busy,
  onChanged,
  onError,
}: {
  question: QuestionWithContext;
  busy: boolean;
  onChanged: (question: QuestionWithContext) => void;
  onError: (message: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [key, setKey] = useState("");
  const [uploading, setUploading] = useState(false);

  const upload = async (file: File) => {
    setUploading(true);
    try {
      onChanged(await uploadQuestionFigure(question.id, file));
    } catch (err) {
      onError(
        err instanceof Error
          ? `Could not upload the figure: ${err.message}`
          : "Could not upload the figure.",
      );
    } finally {
      setUploading(false);
    }
  };

  const patch = async (input: { requires_figure?: boolean; figure_r2_key?: string }) => {
    try {
      onChanged(await updateQuestion(question.id, input));
      setKey("");
    } catch (err) {
      onError(err instanceof Error ? err.message : "Could not update the question.");
    }
  };

  return (
    <div className="rounded-md border border-violation/30 bg-violation-soft p-3">
      <div className="flex items-start gap-2">
        <ImageOff className="mt-0.5 size-3.5 shrink-0 text-violation" />
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-xs font-medium text-ink">
            This question refers to a figure that is not stored.
          </p>
          <p className="text-2xs text-ink-muted">
            The student would meet a question they cannot answer, so it cannot be
            approved or attached to a paper as it stands.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void upload(file);
                e.target.value = "";
              }}
            />
            <Button
              variant="primary"
              size="sm"
              icon={<Upload />}
              disabled={busy || uploading}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? "Uploading…" : "Upload the figure"}
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => void patch({ requires_figure: false })}
            >
              It doesn&apos;t need a figure
            </Button>
          </div>

          <div className="flex items-end gap-2">
            <Input
              label="…or an R2 key that already exists"
              value={key}
              placeholder="questions/<id>/figure.png"
              onChange={(e) => setKey(e.target.value)}
              className="w-72"
            />
            <Button
              size="sm"
              disabled={busy || key.trim() === ""}
              onClick={() => void patch({ figure_r2_key: key.trim() })}
            >
              Use this key
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Removing a stored figure. Worth its own affordance because the consequence is
 * not obvious: the API demotes an approved question straight back to pending,
 * since it still claims to need a figure the student would never see.
 */
function FigureActions({
  question,
  busy,
  onChanged,
  onError,
}: {
  question: QuestionWithContext;
  busy: boolean;
  onChanged: (question: QuestionWithContext) => void;
  onError: (message: string) => void;
}) {
  const [working, setWorking] = useState(false);

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
      <span className="text-2xs text-ink-muted">
        A figure is stored for this question.
      </span>
      <Button
        size="sm"
        variant="danger"
        icon={<Trash2 />}
        disabled={busy || working}
        onClick={async () => {
          setWorking(true);
          try {
            onChanged(await deleteQuestionFigure(question.id));
          } catch (err) {
            onError(
              err instanceof Error ? err.message : "Could not remove the figure.",
            );
          } finally {
            setWorking(false);
          }
        }}
      >
        Remove the figure
      </Button>
      <span className="text-2xs text-ink-faint">
        An approved question drops back to pending when its figure goes.
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Editor                                                              */
/* ------------------------------------------------------------------ */

interface EditorPatch {
  body_md: string;
  options: Array<{ key: string; text: string }>;
  correct_answer: CorrectAnswer;
  solution_md: string | null;
  /** Blank means "clear it"; the API maps "" to NULL. */
  video_url: string;
  reading_url: string;
  /** CODE only. Absent for every other type. */
  starter_code?: string | null;
  tests?: CodeTest[] | null;
  difficulty: Difficulty;
}

/**
 * Edits what is usually wrong with a generated question: the wording, the
 * options, which one is right, and the worked solution.
 *
 * `key_verified` is deliberately **not** here. Confirming a key is an
 * approval-time act; buried in a form with eight other fields is exactly how it
 * would get ticked absent-mindedly.
 */
function QuestionEditor({
  question,
  busy,
  onSave,
  onCancel,
}: {
  question: QuestionWithContext;
  busy: boolean;
  onSave: (patch: EditorPatch) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [body, setBody] = useState(question.body_md);
  const [options, setOptions] = useState(question.options);
  const [solution, setSolution] = useState(question.solution_md ?? "");
  const [videoUrl, setVideoUrl] = useState(question.video_url ?? "");
  const [topicVideo, setTopicVideoDraft] = useState(
    question.topic_video_url ?? "",
  );
  const [starter, setStarter] = useState(question.starter_code ?? "");
  const [testsText, setTestsText] = useState(() =>
    question.tests ? JSON.stringify(question.tests, null, 2) : "",
  );
  const [testsError, setTestsError] = useState<string | null>(null);
  const [topicSaving, setTopicSaving] = useState(false);
  const [readingUrl, setReadingUrl] = useState(question.reading_url ?? "");
  const [difficulty, setDifficulty] = useState<Difficulty>(question.difficulty);
  const [answer, setAnswer] = useState<CorrectAnswer>(question.correct_answer);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bodyRef.current?.focus();
  }, []);

  const submit = () =>
    void onSave({
      body_md: body,
      options,
      correct_answer: answer,
      solution_md: solution.trim() === "" ? null : solution,
      // Blank clears the link. The API turns "" into NULL, so an emptied field
      // removes it rather than storing an empty string that renders as a dead
      // anchor on the student's result page.
      video_url: videoUrl.trim(),
      reading_url: readingUrl.trim(),
      ...(question.type === "CODE"
        ? {
            starter_code: starter.trim() === "" ? null : starter,
            tests: parsedTests,
          }
        : {}),
      difficulty,
    });

  /**
   * The test list, parsed from what the author typed.
   *
   * JSON rather than a row-per-test form, deliberately: `expect` can be a
   * number, a string, an array or an object, and a form that has to model that
   * either restricts what a question can check or grows a type dropdown nobody
   * wants. The parse error is shown inline and Save is blocked on it, so a
   * malformed list can never be stored — a broken test fails the STUDENT, not
   * the author.
   */
  const parsedTests = (() => {
    if (question.type !== "CODE" || testsText.trim() === "") return null;
    try {
      const value = JSON.parse(testsText) as unknown;
      return Array.isArray(value) ? (value as EditorPatch["tests"]) : null;
    } catch {
      return null;
    }
  })();

  const testsInvalid =
    question.type === "CODE" &&
    testsText.trim() !== "" &&
    parsedTests === null;

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
    }
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div className="space-y-4" onKeyDown={onKeyDown}>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-muted">
              Question (markdown + $LaTeX$)
            </span>
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              className="w-full rounded-md border border-border-strong bg-surface px-2.5 py-2 font-mono text-xs text-ink"
            />
          </label>

          {options.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-ink-muted">
                Options
                <span className="ml-1.5 font-normal text-ink-faint">
                  {options.length}
                </span>
              </p>
              {options.map((option, i) => {
                const isCorrect =
                  answer.type === "MCQ"
                    ? answer.key === option.key
                    : answer.type === "MSQ"
                      ? answer.keys.includes(option.key)
                      : false;

                return (
                  <div key={option.key} className="flex items-start gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (answer.type === "MCQ") {
                          setAnswer({ type: "MCQ", key: option.key });
                        } else if (answer.type === "MSQ") {
                          const next = isCorrect
                            ? answer.keys.filter((k) => k !== option.key)
                            : [...answer.keys, option.key].sort();
                          // MSQ must keep at least one correct key.
                          setAnswer({
                            type: "MSQ",
                            keys: next.length > 0 ? next : [option.key],
                          });
                        }
                      }}
                      className={
                        isCorrect
                          ? "mt-1 flex size-5 shrink-0 items-center justify-center rounded-sm border border-answered bg-answered-soft text-2xs font-medium text-answered"
                          : "mt-1 flex size-5 shrink-0 items-center justify-center rounded-sm border border-border-strong text-2xs text-ink-faint"
                      }
                      aria-pressed={isCorrect}
                      aria-label={`Mark option ${option.key} correct`}
                    >
                      {option.key}
                    </button>
                    <textarea
                      value={option.text}
                      rows={1}
                      onChange={(e) =>
                        setOptions((prev) =>
                          prev.map((o, oi) =>
                            oi === i ? { ...o, text: e.target.value } : o,
                          ),
                        )
                      }
                      className="min-h-8 w-full rounded-md border border-border-strong bg-surface px-2.5 py-1.5 font-mono text-xs text-ink"
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <Input
                label="Correct value"
                type="number"
                step="any"
                value={answer.type === "NAT" ? String(answer.value) : ""}
                onChange={(e) =>
                  setAnswer({
                    type: "NAT",
                    value: Number(e.target.value),
                    tolerance: answer.type === "NAT" ? answer.tolerance : 0,
                  })
                }
              />
              <Input
                label="Tolerance"
                type="number"
                step="any"
                min={0}
                value={answer.type === "NAT" ? String(answer.tolerance) : "0"}
                onChange={(e) =>
                  setAnswer({
                    type: "NAT",
                    value: answer.type === "NAT" ? answer.value : 0,
                    tolerance: Math.max(0, Number(e.target.value)),
                  })
                }
              />
            </div>
          )}

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-muted">Solution</span>
            <textarea
              value={solution}
              onChange={(e) => setSolution(e.target.value)}
              rows={6}
              className="w-full rounded-md border border-border-strong bg-surface px-2.5 py-2 font-mono text-xs text-ink"
            />
          </label>

          {question.type === "CODE" ? (
            <div className="space-y-2 rounded-md border border-border bg-surface-sunken p-3">
              <p className="text-xs font-medium text-ink">Code question</p>

              <label className="flex flex-col gap-1">
                <span className="text-2xs font-medium text-ink-muted">
                  Starter code
                </span>
                <span className="text-2xs text-ink-faint">
                  What the editor opens with. A signature and a comment beats a
                  blank editor — a blank one makes the student invent the
                  interface as well as the answer, and then the tests fail for
                  the wrong reason.
                </span>
                <textarea
                  value={starter}
                  onChange={(e) => setStarter(e.target.value)}
                  rows={6}
                  spellCheck={false}
                  className="w-full rounded-md border border-border-strong bg-surface px-2.5 py-2 font-mono text-xs text-ink"
                />
              </label>

              <label className="flex flex-col gap-1">
                <span className="text-2xs font-medium text-ink-muted">
                  Tests (JSON)
                </span>
                <span className="text-2xs text-ink-faint">
                  {`[{ "name": "counts to 5", "call": "countTo(5)", "expect": 5 }]`}
                  {" — "}
                  the student sees these, expectations included: the code runs
                  in their browser, so the comparison values have to be there.
                </span>
                <textarea
                  value={testsText}
                  onChange={(e) => {
                    setTestsText(e.target.value);
                    setTestsError(null);
                  }}
                  rows={7}
                  spellCheck={false}
                  className={cn(
                    "w-full rounded-md border bg-surface px-2.5 py-2 font-mono text-xs text-ink",
                    testsInvalid ? "border-violation" : "border-border-strong",
                  )}
                />
                {testsInvalid ? (
                  <span className="text-2xs text-violation">
                    That is not valid JSON, or not an array. Saving is blocked —
                    a broken test list fails the student, not you.
                  </span>
                ) : null}
                {testsError ? (
                  <span className="text-2xs text-violation">{testsError}</span>
                ) : null}
              </label>
            </div>
          ) : null}

          {/* Shown to the student on their RESULT page when they get this
              wrong — never during the exam, where it would be an answer key
              with extra steps. A question with no link falls back to its
              topic's video. */}
          <div className="grid gap-2 sm:grid-cols-2">
            <Input
              label="Video for this question"
              hint="Shown if they get it wrong. Falls back to the topic's video."
              placeholder="https://youtube.com/watch?v=…"
              value={videoUrl}
              type="url"
              spellCheck={false}
              onChange={(e) => setVideoUrl(e.target.value)}
            />
            <Input
              label="Something to read"
              hint="Optional. A page is better than a video for looking up."
              placeholder="https://developer.mozilla.org/…"
              value={readingUrl}
              type="url"
              spellCheck={false}
              onChange={(e) => setReadingUrl(e.target.value)}
            />
            <div className="sm:col-span-2 flex items-end gap-2">
              <div className="min-w-0 flex-1">
                <Input
                  label={`Video for the whole topic — ${question.topic_name}`}
                  hint="Used by every question in this topic that has no video of its own."
                  placeholder="https://youtube.com/watch?v=…"
                  value={topicVideo}
                  type="url"
                  spellCheck={false}
                  onChange={(e) => setTopicVideoDraft(e.target.value)}
                />
              </div>
              {/* Saved on its own, not with the question: it belongs to the
                  topic, and saving it alongside a question edit would make an
                  edit to one question silently change thirty others without
                  saying so. */}
              <Button
                size="sm"
                className="mb-0.5"
                disabled={topicSaving || topicVideo === (question.topic_video_url ?? "")}
                onClick={async () => {
                  setTopicSaving(true);
                  try {
                    await setTopicVideo(question.topic_id, topicVideo);
                  } finally {
                    setTopicSaving(false);
                  }
                }}
              >
                {topicSaving ? "Saving…" : "Save topic video"}
              </Button>
            </div>
          </div>

          <Select
            label="Difficulty"
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value as Difficulty)}
            className="w-40"
          >
            {Difficulty.options.map((value) => (
              <option key={value} value={value}>
                {labelFor(DIFFICULTY_LABEL, value)}
              </option>
            ))}
          </Select>
        </div>

        <div className="min-w-0 rounded-md border border-border bg-surface-sunken p-3">
          <p className="mb-2 text-2xs font-medium text-ink-muted">Live preview</p>
          <QuestionView
            question={{
              ...question,
              body_md: body,
              options,
              correct_answer: answer,
              solution_md: solution.trim() === "" ? null : solution,
              difficulty,
            }}
            showAnswer
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Blocked on a malformed test list: a broken test fails the STUDENT,
            and they cannot do anything about it mid-exam. */}
        <Button
          variant="primary"
          disabled={busy || testsInvalid}
          onClick={submit}
        >
          Save changes
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <span className="text-2xs text-ink-faint">⌘↩ to save · Esc to cancel</span>
      </div>
    </div>
  );
}

function plainTitle(bodyMd: string): string {
  const line = bodyMd.split("\n").find((l) => l.trim().length > 0) ?? "";
  const clean = line.replace(/[$*_`#]/g, "").trim();
  return clean.length > 70 ? `${clean.slice(0, 70)}…` : clean;
}
